"use client";

import { Container } from "@/src/components/container";

import { ImageProps, LinkProps } from "@/src/common-types";
import { Button } from "@/src/components/button";
import { CustomLink } from "@/src/components/custom-link";

import { cn } from "@/src/utils/shadcn";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./hero.module.css";
import SwiperCore, { EffectFade } from "swiper";
import { Swiper as SwiperType, Navigation } from "swiper";
import { IsoDialog } from "@/src/components/banner-dailog/iso";
import "swiper/swiper-bundle.min.css";
import { useRef } from "react";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { heroData_en } from "@/data/en/hero/v1";
import { heroData_ar } from "@/data/ar/hero/v1";
import { isoSectionData } from "@/data/en/iso";
import { AwardDialog } from "@/src/components/banner-dailog/award";
import { CertificateDialog } from "@/src/components/banner-dailog/certificate";
import { awardSectionData_en } from "@/data/en/award";
import { certificateSectionData_en } from "@/data/en/certificate";
import { awardSectionData_ar } from "@/data/ar/award";
import { certificateSectionData_ar } from "@/data/ar/certificate";
SwiperCore.use([EffectFade, Navigation]);

const navigationButtonCommonClasses = cn(
  "w-[60px] relative z-40 h-[60px] grid place-items-center leading-none text-[1.25rem] bg-accent-900 hover:bg-primary transition-all duration-300 text-white rounded-full"
);

export interface HeroProps {
  items: {
    title: string;
    image: Omit<ImageProps, "width" | "height">;
    button: LinkProps;
  }[];
  params:{
    lang: string
  }
}

export function Hero({params}: HeroProps) {
  const isArabic = params.lang === "ar";

  const awardSectionData = 
    isArabic ? awardSectionData_ar : awardSectionData_en;
  const certificateSectionData =
    isArabic ? certificateSectionData_ar : certificateSectionData_en;

  const swiperRef = useRef<SwiperType>();
  const { items } = isArabic ? heroData_ar : heroData_en;
  return (
    <section className={styles["hero"]}>
      {items && items.length > 0 && (
        <Swiper
          effect="fade"
          loop
          speed={300}
          autoplay={{ delay: 3000 }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index}>
              <div
                className={cn(
                  "relative flex items-center justify-center overflow-hidden py-[9.375rem] lg:min-h-screen min-h-screen bg-black/20 "
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 -z-1   bg-cover bg-no-repeat bg-blend-luminosity [background-position:top_center] [transform:scale(1)] [transition:7000ms_ease,opacity_1500ms_ease-in]"
                  )}
                  style={{ backgroundImage: `url(${item.image.src})` }}
                />

                <div className="flex flex-col justify-center">
                  <Container>
                    <div
                      className={cn(
                        "relative z-10 mx-auto max-w-[800px] text-center text-white dark:text-white lg:mt-[60px]",
                        styles["hero-content"]
                      )}
                    >
                      <div className="space-y-6 md:space-y-8">
                        <h1 className={`font-secondary font-semibold uppercase leading-[1.1] ${isArabic ? 'text-2xl md:text-3xl lg:text-3xl font-light' : 'text-2xl md:text-3xl lg:text-4xl'}`}>
                          {item.title}
                        </h1>
                        {/* <Button asChild className={cn("rounded-full")}>
                          <CustomLink
                            aria-label={item.button.label}
                            href={item.button.href}
                            openNewTab={item.button.openNewTab}
                          >
                            <span>{item.button.label}</span>
                            <svg
                              width={28}
                              height={9}
                              viewBox="0 0 28 9"
                              fill="currentColor"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M27.7911 5.02543C27.9863 4.83016 27.9863 4.51358 27.7911 4.31832L24.6091 1.13634C24.4138 0.941077 24.0972 0.941077 23.902 1.13634C23.7067 1.3316 23.7067 1.64818 23.902 1.84345L26.7304 4.67187L23.902 7.5003C23.7067 7.69556 23.7067 8.01214 23.902 8.20741C24.0972 8.40267 24.4138 8.40267 24.6091 8.20741L27.7911 5.02543ZM0.4375 5.17188L27.4375 5.17187L27.4375 4.17187L0.4375 4.17188L0.4375 5.17188Z" />
                            </svg>
                          </CustomLink>
                        </Button> */}
                      </div>
                    </div>
                  </Container>
                  <div className="absolute left-1/2 transform -translate-x-1/2 bottom-16 lg:bottom-10 mx-auto w-[320px] md:max-w-[800px] text-center place-items-center flex justify-center gap-1">
                  <div className="p-2.5">
                  <IsoDialog {...isoSectionData} />
                  </div>
                  <div className="p-2.5">
                    <AwardDialog {...awardSectionData} />
                  </div>
                  <div className="p-2.5">
                    <CertificateDialog {...certificateSectionData} />
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}

          <div className="absolute right-0 top-[50%] z-50 hidden h-max w-full [transform:translateY(-50%)] lg:block">
            <Container>
              <div className="ml-auto grid max-w-max gap-2.5 px-4">
                <button
                  className={cn(navigationButtonCommonClasses)}
                  onClick={() => swiperRef.current?.slidePrev()}
                  aria-label="slide prev"
                >
                  <FaArrowLeftLong />
                </button>
                <button
                  className={cn(navigationButtonCommonClasses)}
                  onClick={() => swiperRef.current?.slideNext()}
                  aria-label="slide next"
                >
                  <FaArrowRightLong />
                </button>
              </div>
            </Container>
          </div>
        </Swiper>
      )}
    </section>
  );
}
